import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { AdminTenant } from '@terafile/shared'
import { type FormEvent, useState } from 'react'
import { updateTenantPlan } from '../lib/admin-api.js'
import { SUBMIT_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { ModalOverlay } from './ModalOverlay.js'

type Props = {
  tenant: AdminTenant | null
  onClose: () => void
}

export function UpdateTenantPlanModal({ tenant, onClose }: Props) {
  const queryClient = useQueryClient()
  const [plan, setPlan] = useState<'FREE' | 'PRO'>('FREE')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (id: string) => updateTenantPlan(id, { plan }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
      queryClient.invalidateQueries({ queryKey: ['admin-tenant'] })
      onClose()
    },
    onError: (err) => {
      setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })

  if (!tenant) return null

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    mutation.mutate(tenant.id)
  }

  return (
    <ModalOverlay className="bg-black/50">
      <div className="bg-background border border-border rounded-lg shadow-lg p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold">プラン手動切替</h2>
        <p className="text-xs text-muted-foreground mt-1">
          {tenant.name ?? '(無名)'} · 現在 {tenant.plan}
        </p>
        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          <div>
            <p className="text-sm font-medium mb-2">プラン</p>
            <div className="flex gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="plan"
                  value="FREE"
                  checked={plan === 'FREE'}
                  onChange={() => setPlan('FREE')}
                />
                <span className="text-sm">無料</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="plan"
                  value="PRO"
                  checked={plan === 'PRO'}
                  onChange={() => setPlan('PRO')}
                />
                <span className="text-sm">有料</span>
              </label>
            </div>
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={mutation.isPending}
              className="px-4 py-2 border border-border rounded-md font-medium"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium disabled:opacity-50"
            >
              {mutation.isPending ? '更新中…' : '更新'}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  )
}
